import type {
  CharacterDetails,
  CharacterSheet,
  ResourceAction,
  ResourceProblem,
  RollReason,
  SheetFeature,
} from '@rollcall/contracts'
import { ABILITY_KEYS, ABILITY_NAMES } from '@rollcall/rules'
import { Description } from './description.js'
import { CharacterCode } from './character-code.js'
import { useT } from './i18n-context.js'
import {
  TRAIT_KINDS,
  contentName,
  featureName,
  traitKind,
  traitLabel,
  valueLabel,
} from './labels.js'
import { ProvenanceProvider, ValueButton, signedNumber } from './provenance.js'
import { DetailsSection } from './details.js'
import { OwnResources, ResourceRow } from './resources.js'

/**
 * Вкладка «Персонаж»: лист целиком.
 *
 * Ни одного расчёта здесь нет. Всё, что показано, посчитано сервером
 * свёрткой журнала (D4) и пришло вместе с провенансом (D5): если бы
 * компонент считал сам, лист расходился бы с сервером ровно в тот
 * день, когда правило поменяется в одном месте из двух (CLAUDE.md,
 * п. 5). Проверяется тестом: с подменённой свёрткой вкладка покажет
 * заведомо неверные числа как есть.
 *
 * Каждое число — `ValueButton`, то есть открывает разбор. Величины,
 * которые не попали ни в один раздел, показываются в конце: молча
 * потерянная величина хуже некрасивой строки.
 */

/**
 * Что показывает вкладка и в каком порядке.
 *
 * Подписи величин собраны в `labels.ts`: их спрашивает и разбор,
 * и журнал бросков, и держать три списка вместо одного значит
 * однажды назвать одну величину тремя способами.
 *
 * Величина без подписи не прячется, а показывается в «прочих» под
 * своим путём: молча потерянная величина хуже некрасивой строки.
 * Это единственное место, где машинный путь может дойти до экрана,
 * и означает оно ровно одно — движок научился считать что-то, чему
 * подписи ещё не завели.
 */

/** Порядок сводки: то, на что смотрят в бою, — первым. */
const SUMMARY = [
  'ac',
  'hp.max',
  'initiative',
  'speed',
  'proficiency_bonus',
  'senses.darkvision',
] as const

/** Величины-модификаторы: показываются со знаком. */
const SIGNED = new Set<string>(['initiative', 'proficiency_bonus'])

/** Наборы признаков: показываются списком, а не числом. */
const TRAIT_SETS = [
  'proficiencies',
  'languages',
  'resistances',
  'advantages',
  'disadvantages',
] as const

/**
 * Заклинательские числа: показываются, только если персонаж колдует.
 * У воина их в листе нет вовсе — не ноль, а нет.
 */
const SPELL_VALUES = ['spell.save_dc', 'spell.attack'] as const

/**
 * Атаки и урон живут на вкладке «Бой» — там они с кнопками броска
 * и записью кости. Здесь их прятать нечем, кроме этого правила:
 * иначе те же величины пришли бы вторым списком под машинными
 * путями («attack.dagger»).
 */
function isAttackValue(path: string): boolean {
  return path.startsWith('attack.') || path.startsWith('damage.')
}

export type CharacterSheetTabProps = {
  readonly sheet: CharacterSheet
  /**
   * Бросок величины (D12). Не обязателен: лист читается и без
   * роллера — на нём и до него смотрели одни числа.
   */
  readonly onRoll?: ((reason: RollReason) => void) | undefined
  /** Счётчик ресурса (D56); `undefined` — лист открыт без права правки. */
  readonly onResource?: ((action: ResourceAction) => void) | undefined
  /** Отказ по счётчику: «ярость кончилась», «и так целиком». */
  readonly problems?: readonly ResourceProblem[] | undefined
  /** Правка имени и описания (D4); `undefined` — правки нет. */
  readonly onDetails?:
    | ((patch: { name: string | null; details: CharacterDetails | null }) => void)
    | undefined
  readonly busy?: boolean | undefined
}

export function CharacterSheetTab({
  sheet,
  onRoll,
  onResource,
  onDetails,
  problems = [],
  busy = false,
}: CharacterSheetTabProps) {
  const t = useT()
  const label = (path: string) => valueLabel(t, path) ?? path

  // Пути, которые уже показаны разделами: остальное уйдёт в «прочее».
  const shown = new Set<string>([
    'level',
    ...SUMMARY,
    ...TRAIT_SETS,
    ...SPELL_VALUES,
    ...ABILITY_NAMES.map((name) => `abilities.${ABILITY_KEYS[name]}.score`),
    // Проверка характеристики — тот же модификатор, что на карточке
    // характеристики, и вторым списком он не нужен: бросают его
    // кнопкой на самой карточке.
    ...ABILITY_NAMES.map((name) => `check.${ABILITY_KEYS[name]}`),
    // Хиты за уровень видны в разборе максимума хитов, где им и место.
    ...Object.keys(sheet.values).filter((path) => path.startsWith('hp.level_')),
    ...sheet.saves.map((save) => save.path),
    ...sheet.skills.map((skill) => skill.path),
  ])
  const rest = Object.keys(sheet.values).filter(
    (path) => !shown.has(path) && !isAttackValue(path),
  )
  const caster = SPELL_VALUES.every((path) => sheet.values[path] !== undefined)

  // Оформление под класс (D28, п. 4) — слой поверх темы, а не правка
  // компонента: лист сообщает разметке, какой класс у персонажа,
  // и на этом его участие кончается. Цвета, пороги контраста
  // и решение «текстом или только рамкой» живут в `brand.css`.
  // Класс берётся из источников листа тем же способом, что и на
  // вкладке заклинаний: списка классов у клиента нет и быть не должно.
  const classId = Object.keys(sheet.sources).find(
    (id) => sheet.sources[id]?.type === 'class',
  )

  return (
    <ProvenanceProvider view={{ values: sheet.values, sources: sheet.sources }}>
      <section
        className="app-screen"
        aria-labelledby="sheet-title"
        data-class={classId}
      >
        {/* Имя персонажа стоит в шапке приложения, и второй раз
            заголовком вкладки его писать незачем: там оно видно
            с любой вкладки, а не только с этой. */}
        <h2 id="sheet-title">{t('tabs.character')}</h2>

        {/* Драфт виден сразу (D29): персонаж без кампании считается
            и показывается как обычный, и единственное, чем он от него
            отличается, — тем, что стола за ним нет. Молчать об этом
            значит дать человеку думать, что он в партии. */}
        {sheet.character.campaignId === null && (
          <p className="app-draft" data-testid="draft-mark">
            {t('sheet.draft')}
          </p>
        )}

        <CharacterCode code={sheet.character.code} />
        <p className="app-slot-hint">{t('sheet.codeHint')}</p>

        <div className="app-grid">
          <ValueButton path="level" label={t('sheet.level')} />
          {SUMMARY.map((path) => (
            <ValueButton
              key={path}
              path={path}
              label={label(path)}
              // Модификаторы показываются со знаком — на вкладке «Бой»
              // они такие же, и число на двух вкладках обязано
              // выглядеть одинаково.
              signed={SIGNED.has(path)}
            />
          ))}
        </div>

        {/* Крупно модификатор, мелко значение: модификатором
            пользуются в каждом броске, значением — только при
            повышении уровня. Разбор открывается у значения: именно
            оно складывается из выбора и прибавок, а модификатор
            из него выводится. */}
        <section aria-labelledby="sheet-abilities">
          <h3 id="sheet-abilities">{t('sheet.abilities')}</h3>
          {/* Отдельный класс — не «сетка характеристик пошире»,
              а место заклёпки: деталь стали живёт ровно здесь
              (D28, п. 4). */}
          <div className="app-grid app-grid-abilities">
            {ABILITY_NAMES.map((name) => (
              <ValueButton
                key={name}
                path={`abilities.${ABILITY_KEYS[name]}.score`}
                label={t(`abilities.${name}`)}
                display={signedNumber(sheet.context.abilities[ABILITY_KEYS[name]].mod)}
                note={t('sheet.score', {
                  value: sheet.values[`abilities.${ABILITY_KEYS[name]}.score`]?.value,
                })}
                {...(onRoll === undefined
                  ? {}
                  : {
                      // Бросают проверку характеристики, а не само
                      // значение: «Strength check» — это d20
                      // с модификатором (с. 6).
                      onRoll: () => {
                        onRoll({
                          kind: 'check',
                          subject: `check.${ABILITY_KEYS[name]}`,
                        })
                      },
                      rollLabel: t('roll.roll'),
                      rollBusy: busy,
                    })}
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="sheet-saves">
          <h3 id="sheet-saves">{t('sheet.saves')}</h3>
          <div className="app-grid">
            {sheet.saves.map((save) => (
              <ValueButton
                key={save.path}
                path={save.path}
                label={t(`abilities.${save.ability}`)}
                signed
                proficient={save.proficient}
                note={save.proficient ? t('sheet.proficient') : undefined}
                {...(onRoll === undefined
                  ? {}
                  : {
                      onRoll: () => {
                        onRoll({ kind: 'save', subject: save.path })
                      },
                      rollLabel: t('roll.roll'),
                      rollBusy: busy,
                    })}
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="sheet-skills">
          <h3 id="sheet-skills">{t('sheet.skills')}</h3>
          <div className="app-list">
            {sheet.skills.map((skill) => (
              <ValueButton
                key={skill.path}
                path={skill.path}
                label={contentName(t, skill.id, skill.name)}
                signed
                proficient={skill.proficient}
                note={
                  skill.proficient
                    ? t('sheet.proficientIn', {
                        ability: t(`abilities.${skill.ability}`),
                      })
                    : t(`abilities.${skill.ability}`)
                }
                {...(onRoll === undefined
                  ? {}
                  : {
                      onRoll: () => {
                        onRoll({ kind: 'check', subject: skill.path })
                      },
                      rollLabel: t('roll.roll'),
                      rollBusy: busy,
                    })}
              />
            ))}
          </div>
        </section>

        {caster && (
          <section aria-labelledby="sheet-spellcasting">
            <h3 id="sheet-spellcasting">{t('spells.numbers')}</h3>
            <div className="app-grid">
              <ValueButton path="spell.save_dc" label={t('spells.saveDc')} />
              <ValueButton path="spell.attack" label={t('spells.attack')} signed />
            </div>
          </section>
        )}

        <section aria-labelledby="sheet-traits">
          <h3 id="sheet-traits">{t('sheet.traits')}</h3>
          <div className="app-list">
            {TRAIT_SETS.map((path) => (
              <TraitSet key={path} sheet={sheet} path={path} label={label(path)} />
            ))}
          </div>
        </section>

        {rest.length > 0 && (
          <section aria-labelledby="sheet-rest">
            <h3 id="sheet-rest">{t('sheet.rest')}</h3>
            <div className="app-list">
              {rest.map((path) => (
                <ValueButton key={path} path={path} label={label(path)} />
              ))}
            </div>
          </section>
        )}

        <Features
          features={sheet.features}
          sources={sheet.sources}
          onResource={onResource}
          busy={busy}
        />

        {problems.length > 0 && (
          <ul className="app-problems" data-testid="resource-problems">
            {problems.map((problem) => (
              <li key={`${problem.code}:${problem.subject ?? ''}`}>
                {problem.message}
              </li>
            ))}
          </ul>
        )}

        <OwnResources resources={sheet.resources} onAction={onResource} busy={busy} />

        <DetailsSection character={sheet.character} onSave={onDetails} busy={busy} />
      </section>
    </ProvenanceProvider>
  )
}

/**
 * Набор признаков по видам.
 *
 * Одной строкой владения не читаются: «Атлетика, Восприятие, кости,
 * дварфийский, Сила, Телосложение, лёгкие доспехи, простое оружие» —
 * это четыре разных списка, слитых в один. Вид признака известен
 * приставке ключа (D21.3), по ней они и раскладываются.
 *
 * Разбор при этом остаётся один на всю величину: она посчитана
 * целиком, и делить провенанс по группам значило бы выдумывать
 * четыре величины там, где движок считает одну.
 */
function TraitSet({
  sheet,
  path,
  label,
}: {
  readonly sheet: CharacterSheet
  readonly path: string
  readonly label: string
}) {
  const t = useT()
  const value = sheet.values[path]?.value
  const traits = Array.isArray(value) ? value : []

  if (traits.length === 0) {
    return <ValueButton path={path} label={label} />
  }

  const groups = TRAIT_KINDS.map((kind) => ({
    kind,
    items: traits.filter((trait) => traitKind(path, String(trait)) === kind),
  })).filter((group) => group.items.length > 0)

  return (
    <div className="app-trait-set" data-trait-set={path}>
      {/* Крупным числом — сколько признаков в наборе: разбор
          у величины один, а список стоит под ним по видам. */}
      <ValueButton path={path} label={label} display={String(traits.length)} />
      <dl className="app-trait-groups">
        {groups.map((group) => (
          <div key={group.kind} className="app-trait-group">
            <dt>{t(`sheet.traitKind.${group.kind}`)}</dt>
            <dd>
              {group.items
                .map((trait) => traitLabel(t, path, String(trait)))
                .join(', ')}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * Особенности с текстом: черты вида, предыстория, особенности класса.
 *
 * Текст — из записи контента, на языке источника. Перевод SRD 5.2 —
 * отдельная работа, и она записана открытым вопросом
 * (`docs/open-questions.md`), а не сделана наполовину здесь.
 */
function Features({
  features,
  sources,
  onResource,
  busy,
}: {
  readonly features: readonly SheetFeature[]
  readonly sources: CharacterSheet['sources']
  readonly onResource?: ((action: ResourceAction) => void) | undefined
  readonly busy?: boolean | undefined
}) {
  const t = useT()
  if (features.length === 0) return null

  return (
    <section aria-labelledby="sheet-features">
      <h3 id="sheet-features">{t('sheet.features')}</h3>
      <div className="app-features">
        {features.map((feature) => (
          <article key={`${feature.source}:${feature.key}`} className="app-feature">
            {/* Имя особенности — из записи контента: переводятся
                термины (`terms.ts`), а описание — компонентом ниже. */}
            <h4>{featureName(t, feature.key, feature.name, feature.source)}</h4>
            <p className="app-feature-source">
              {contentName(t, feature.source, sources[feature.source]?.name)}
              {feature.level !== null &&
                ` · ${t('sheet.levelShort', { level: feature.level })}`}
            </p>
            <Description text={feature.text} textRu={feature.textRu} />
            {/* Счётчик стоит под своей особенностью (D56): ярость —
                под «Rage», а не общим списком в стороне. */}
            {feature.resources.map((resource) => (
              <ResourceRow
                key={resource.id}
                resource={resource}
                onAction={onResource}
                busy={busy}
              />
            ))}
          </article>
        ))}
      </div>
    </section>
  )
}
